/* ============================================================================
 * Cermin `routers/sales.py::rincian_bayar` (batch UANG-KASIR N0, 23 Sep 2026).
 *
 * KENAPA ADA: layar Input Nota menyarankan angka yang tidak pernah dipakai
 * server — placeholder Harga Terima = total + PPN SEBELUM diskon/admin, dan
 * petunjuk DP mengabaikan admin & trade-in. Nota produksi 760bef0c diketik
 * persis angka placeholder itu. Sejak N0 server menolak ketikan yang tidak
 * cocok dengan rincian, jadi layar WAJIB menyarankan angka dari rumus yang
 * sama — kalau tidak, kasir yang menurut layar justru ditolak.
 *
 * Kesetaraan dengan Python dijaga `backend/tests/test_n0_rincian_js_sama.py`
 * (menjalankan berkas ini lewat node atas kisi nilai). Jangan ubah urutan
 * operasinya tanpa mengubah Python-nya juga.
 * ========================================================================== */

export const TOLERANSI_HARGA_TERIMA = 1.0;

/* Python `round(x, n)` membulatkan .5 ke GENAP; `Math.round` ke atas. Untuk PPN
 * rupiah penuh (dpp × 11%), dpp berakhiran ...550 jatuh tepat di .5 — tanpa
 * cermin ini layar dan server berselisih Rp 1. */
export function bulatkanSepertiPython(x, desimal = 0) {
  const f = 10 ** desimal;
  const v = x * f;
  const bawah = Math.floor(v);
  const selisih = v - bawah;
  let hasil;
  if (selisih === 0.5) hasil = bawah % 2 === 0 ? bawah : bawah + 1;
  else hasil = Math.round(v);
  return hasil / f;
}

export function hitungPpn(dpp, persen) {
  if (!persen || persen <= 0 || !dpp) return 0;
  return bulatkanSepertiPython((Number(dpp) * Number(persen)) / 100);
}

/* DPP sebuah nota = nilai barang SESUDAH diskon — cermin
 * `routers/sales.py::pajak_nota` (keputusan owner 25 Sep 2026, pertanyaan 26f:
 * "PPN dihitung dari harga SESUDAH diskon"). SATU-SATUNYA sumber argumen
 * `hitungPpn` di layar: dijaga tes AST `test_k1_ppn_sesudah_diskon.py` —
 * pratinjau yang menghitung PPN dari total bruto menyarankan angka yang
 * ditolak server. */
export function dppNota(total, diskon) {
  return bulatkanSepertiPython(Math.max(0, (Number(total) || 0) - (Number(diskon) || 0)), 2);
}

export function hitungBiayaAdmin(subtotalSetelahDiskon, mode, nilai) {
  const n = Number(nilai) || 0;
  if (mode === "nominal") return bulatkanSepertiPython(n, 2);
  if (mode === "persen") return bulatkanSepertiPython((subtotalSetelahDiskon * n) / 100, 2);
  return 0;
}

export function rincianBayar({ total = 0, diskon = 0, biayaAdmin = 0, ppn = 0, tradeIn = 0, dp = 0 }) {
  const t = Number(total) || 0;
  const d = Number(diskon) || 0;
  const a = Number(biayaAdmin) || 0;
  const p = Number(ppn) || 0;
  const ti = Number(tradeIn) || 0;
  const u = Number(dp) || 0;
  const subtotal = bulatkanSepertiPython(t - d, 2);
  const nilaiTagihan = bulatkanSepertiPython(subtotal + a + p, 2);
  const harusDibayar = bulatkanSepertiPython(subtotal + a - ti + p, 2);
  return {
    total: t, diskon: d, biayaAdmin: a, ppn: p, tradeIn: ti, dp: u,
    nilaiTagihan,
    harusDibayar,
    diTempat: bulatkanSepertiPython(harusDibayar - u, 2),
    tagihanMinDp: bulatkanSepertiPython(nilaiTagihan - u, 2),
  };
}

/**
 * Rincian layar Input Penjualan (`pages/Sales.jsx`) langsung dari medan form-nya.
 *
 * KENAPA ADA: layar itu dulu menghitung acuan Split Payment sendiri —
 * `(harga_terima ketikan || total barang) − trade-in` saat tanpa diskon/admin/
 * PPN. Sejak N0 placeholder Harga Terima menyarankan angka yang SUDAH dikurangi
 * trade-in; kasir yang menyalinnya membuat layar mengurangi trade-in DUA KALI
 * dan memaksa split sebesar itu. Server tidak memeriksa jumlah split (kecuali
 * baris Kredit Retur), jadi nota tersimpan dengan uang kurang dan posting-nya
 * gagal (tinjauan 24 Sep 2026). Ketikan Harga Terima SENGAJA bukan parameter
 * fungsi ini: acuan uang di layar dihitung dari komponen nota, sama seperti
 * server dan layar Input Nota.
 *
 * Biaya admin & PPN dihitung dengan pembulatan server (lihat di atas), supaya
 * acuan split tidak berselisih Rp 1 dari `harus_dibayar` server.
 * Dijaga `backend/tests/test_n0_rincian_js_sama.py`.
 */
export function rincianFormPenjualan({
  totalBarang = 0, diskon = 0, biayaAdminMode = "", biayaAdminNilai = 0,
  kenaPpn = false, ppnPersen = 11, tradeIn = 0, dp = 0,
}) {
  const t = Number(totalBarang) || 0;
  const d = Number(diskon) || 0;
  const admin = hitungBiayaAdmin(bulatkanSepertiPython(t - d, 2), biayaAdminMode || "", biayaAdminNilai);
  const dpp = dppNota(t, d);
  const ppn = kenaPpn ? hitungPpn(dpp, ppnPersen) : 0;
  return { ...rincianBayar({ total: t, diskon: d, biayaAdmin: admin, ppn, tradeIn, dp }), dpp };
}

/* Kredit Retur — pencocokan SAMA dengan `sales_posting._adalah_kredit_retur`
 * (huruf kecil + rapikan spasi). Diketik di sini (bukan diimpor dari komponen)
 * supaya berkas ini tetap tanpa impor dan bisa dijalankan node. */
const adalahKreditReturNama = (m) =>
  (m || "").trim().toLowerCase().split(/\s+/).join(" ") === "kredit retur";

/**
 * Uang yang SEHARUSNYA diterima kasir di tempat — cermin
 * `routers/sales.py::uang_kasir_nota` (batch N3, D6: pembanding Total
 * Pembayaran). Tunai: diterima di kasir (harus dibayar − DP) dikurangi baris
 * Kredit Retur, tidak pernah negatif. Bertempo: baris non-Kredit-Retur (uang
 * muka), atau `uangMuka` untuk draf bentuk lama tanpa baris.
 * Kesetaraan dengan Python dijaga `backend/tests/test_n3_layar_js.py`.
 */
export function uangKasir(r, rows, { tempo = false, uangMuka = 0 } = {}) {
  const baris = (rows || []).filter(b => (Number(b?.amount) || 0) > 0);
  const kr = baris.filter(b => adalahKreditReturNama(b.method))
    .reduce((a, b) => a + (Number(b.amount) || 0), 0);
  if (tempo) {
    if (baris.length) {
      return bulatkanSepertiPython(baris.filter(b => !adalahKreditReturNama(b.method))
        .reduce((a, b) => a + (Number(b.amount) || 0), 0), 2);
    }
    return bulatkanSepertiPython(Math.max(0, Number(uangMuka) || 0), 2);
  }
  return bulatkanSepertiPython(Math.max(0, (Number(r?.diTempat) || 0) - kr), 2);
}

/** Anak tangga Harga Terima yang diterima server (cermin `anak_tangga_harga_terima`). */
export function anakTangga(r) {
  return [0, r.nilaiTagihan, r.harusDibayar, r.diTempat, r.tagihanMinDp];
}

export function cocokAnakTangga(ht, r) {
  const h = Number(ht) || 0;
  return anakTangga(r).some(v => Math.abs(h - v) <= TOLERANSI_HARGA_TERIMA);
}

/**
 * Label satu baris `_baris_pembayaran` (susunan server
 * `routers/sales.py::susun_baris_pembayaran`) — SATU ejaan untuk detail nota,
 * jendela approve, faktur, dan struk. Trade-in diberi awalan supaya nama unit
 * tidak terbaca sebagai metode bayar.
 */
export function labelBarisBayar(b) {
  if (!b) return "";
  return b.jenis === "trade_in" && b.label !== "Trade-in" ? `Trade-in: ${b.label}` : (b.label || "");
}
